import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { MessageService } from "primeng/api";
import { from, Observable, of, throwError } from "rxjs";
import { catchError, switchMap } from "rxjs/operators";
import { ContractSummary } from "src/app/models/contract-summary/contract-summary";
import { ContractFull } from "src/app/models/contract/contract-full";
import { ContractPendingPayment } from "src/app/models/contract/contract-pending-payment";
import { ContractPendingSignature } from "src/app/models/contract/contract-pending-signature";
import { environment } from "src/environments/environment";
import { ApsPermissionService } from "../aps-permission/aps-permission.service";

@Injectable({
  providedIn: 'root'
})
export class ContractSummaryService {

  private apiUrl = environment.apiUrl;
  private contractSummary: ContractSummary = new ContractSummary;
  /** Indicates the user explicitly navigated forward from this page, so auto-lookups should be skipped on return */
  private dealerDataConfirmed: boolean = false;
  private readonly DEALER_DATA_SNAPSHOT_KEY = 'contractSummaryDealerSnapshot';
  private readonly contractMetricPrefix = 'apsContractMetric';

  constructor(
    private httpClient: HttpClient,
    private messageService: MessageService,
    private apsPermissionService: ApsPermissionService
  ) {
  }

  setContractSummary(contractSummary: ContractSummary) {
    this.contractSummary = contractSummary;
  }

  getContractSummary(): ContractSummary {
    return this.contractSummary;
  }

  getContractSummaryClean() {
    this.contractSummary = new ContractSummary();
    this.dealerDataConfirmed = false;
  }

  confirmDealerData(): void {
    this.dealerDataConfirmed = true;
  }

  isDealerDataConfirmed(): boolean {
    return this.dealerDataConfirmed;
  }

  saveDealerDataSnapshot(): void {
    try {
      const snapshot = {
        dealer: this.contractSummary.dealer,
        dealerSeller: this.contractSummary.dealerSeller,
        typePayment: this.contractSummary.typePayment,
        formaPayment: this.contractSummary.formaPayment,
        prazoPayment: this.contractSummary.prazoPayment,
        timestamp: Date.now()
      };
      sessionStorage.setItem(this.DEALER_DATA_SNAPSHOT_KEY, JSON.stringify(snapshot));
    } catch {
      // sessionStorage unavailable; silently ignore
    }
  }

  restoreDealerDataSnapshot(): boolean {
    try {
      const raw = sessionStorage.getItem(this.DEALER_DATA_SNAPSHOT_KEY);
      if (!raw) {
        return false;
      }

      const snapshot = JSON.parse(raw);
      if (snapshot.dealer) {
        this.contractSummary.dealer = snapshot.dealer;
      }
      if (snapshot.dealerSeller) {
        this.contractSummary.dealerSeller = snapshot.dealerSeller;
      }
      if (snapshot.typePayment !== undefined) {
        this.contractSummary.typePayment = snapshot.typePayment;
      }
      if (snapshot.formaPayment) {
        this.contractSummary.formaPayment = snapshot.formaPayment;
      }
      if (snapshot.prazoPayment) {
        this.contractSummary.prazoPayment = snapshot.prazoPayment;
      }

      sessionStorage.removeItem(this.DEALER_DATA_SNAPSHOT_KEY);
      return true;
    } catch {
      return false;
    }
  }

  save(contractFull: ContractFull, lang: string): Observable<ContractFull> {

    console.log('>> INICIO: Chamando contractSummaryService.save para Contract ID');

    const jsonPayload = JSON.stringify(contractFull);
    const byteSize = new TextEncoder().encode(jsonPayload).length;

    console.log('>> Tamanho do Payload para Contract: ' + byteSize + ' bytes');

    const endpoint = `${this.apiUrl}/api/v1/contracts/full?lang=${lang}`;

    return this.httpClient.post<ContractFull>(endpoint, contractFull)
      .pipe(
        catchError((error) => {
          if (this.isMissingInsuranceCodeError(error)) {
            return throwError(() => this.buildMissingInsuranceCodeError(error));
          }

          if (this.isGoogleReauthError(error)) {
            return throwError(() => this.buildGoogleReauthError(error));
          }

          if (!this.shouldRetryAfterApsForbidden(error)) {
            return throwError(() => error);
          }

          this.trackContractMetric('retry.start');

          // Warm up auth context and APS permission cache before a single retry.
          return from(this.apsPermissionService.ensureUserContextReady())
            .pipe(
              switchMap(() => this.httpClient.get(`${this.apiUrl}/api/permission`)),
              catchError(() => of(null)),
              switchMap(() => this.httpClient.post<ContractFull>(endpoint, contractFull)),
              switchMap((response) => {
                this.trackContractMetric('retry.success');
                return of(response);
              }),
              catchError((retryError) => throwError(() => this.buildApsRetryError(retryError)))
            );
        })
      );
  }

  private buildApsRetryError(error: unknown): unknown {
    this.trackContractMetric('retry.exhausted');
    return Object.assign({}, error, {
      apsRetryExhausted: true,
      apsRetryMessage: 'Falha de autorização APS após nova tentativa automática.'
    });
  }

  private buildMissingInsuranceCodeError(error: unknown): unknown {
    return Object.assign({}, error, {
      contractSaveIssue: 'missingInsuranceCode'
    });
  }

  private buildGoogleReauthError(error: unknown): unknown {
    return Object.assign({}, error, {
      contractSaveIssue: 'googleReauthRequired'
    });
  }

  private trackContractMetric(metric: string): void {
    const key = `${this.contractMetricPrefix}.${metric}`;

    try {
      const current = Number(sessionStorage.getItem(key) || '0');
      const next = current + 1;
      sessionStorage.setItem(key, String(next));
      console.info(`[APS-CONTRACT-METRIC] ${metric}=${next}`);
    } catch {
      console.info(`[APS-CONTRACT-METRIC] ${metric}`);
    }
  }

  private shouldRetryAfterApsForbidden(error: unknown): boolean {
    const status = (error as { status?: number })?.status;
    if (status !== 403) {
      return false;
    }

    const errorPayload = (error as { error?: unknown })?.error;
    const details = typeof errorPayload === 'string'
      ? errorPayload
      : JSON.stringify(errorPayload ?? '');

    const normalizedDetails = details.toLowerCase();
    return normalizedDetails.includes('unexpected error occurred while invoking aps service')
      || normalizedDetails.includes('invalid_scope')
      || normalizedDetails.includes('scope null');
  }

  private isMissingInsuranceCodeError(error: unknown): boolean {
    const status = (error as { status?: number })?.status;
    if (status !== 404) {
      return false;
    }

    const errorPayload = (error as { error?: unknown })?.error;
    const details = typeof errorPayload === 'string'
      ? errorPayload
      : JSON.stringify(errorPayload ?? '');

    const normalizedDetails = details.toLowerCase();
    return normalizedDetails.includes('getinsurancecode')
      && normalizedDetails.includes('null');
  }

  private isGoogleReauthError(error: unknown): boolean {
    const errorPayload = (error as { error?: unknown })?.error;
    const details = typeof errorPayload === 'string'
      ? errorPayload
      : JSON.stringify(errorPayload ?? '');

    const normalizedDetails = details.toLowerCase();
    return normalizedDetails.includes('invalid_grant')
      || normalizedDetails.includes('invalid_rapt')
      || normalizedDetails.includes('oauth2.googleapis.com/token');
  }

  public getContractSaveFriendlyError(error: unknown): string | null {
    if ((error as { contractSaveIssue?: string })?.contractSaveIssue === 'missingInsuranceCode') {
      return 'O produto selecionado nao esta configurado com codigo de seguro. Solicite a correcao do cadastro do produto ou selecione outro produto antes de tentar novamente.';
    }

    if ((error as { contractSaveIssue?: string })?.contractSaveIssue === 'googleReauthRequired') {
      return 'Nao foi possivel concluir a criacao do contrato porque a autenticacao do integrador externo expirou (Google OAuth). Tente novamente em alguns minutos e, se persistir, acione o suporte tecnico para revalidar as credenciais da integracao.';
    }

    return null;
  }

  saveContractPendingPayment(contractPendingPayment: ContractPendingPayment): Observable<ContractPendingPayment> {
    return this.httpClient.post<ContractPendingPayment>(`${this.apiUrl}/api/contracts/pending/payment`, contractPendingPayment);
  }

  saveContractPendingSignature(contractPendingSignature: ContractPendingSignature): Observable<ContractPendingSignature> {
    return this.httpClient.post<ContractPendingSignature>(`${this.apiUrl}/api/v1/contracts/pendingSignature`, contractPendingSignature);
  }
}
